import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MapPin, ChevronRight, Building2, Megaphone } from 'lucide-react';
import { Shul } from '../../types';
import DistanceText from '../directory/DistanceText';
import { activeMessages } from '../../utils/listingDisplay';

interface ShulCardProps {
  shul: Shul;
  /** Meters from the user, after Near me. */
  distance?: number;
}

export default function ShulCard({ shul, distance }: ShulCardProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';

  const mapHref = `/shuls?view=map&shul=${shul.id}`;
  const canShowOnMap = Boolean(shul.lat && shul.lng);
  const name = isHebrew ? shul.nameHe : shul.nameEn;
  const hasMessage = activeMessages(shul.messages, new Date().toISOString()).length > 0;

  return (
    <div className="flex items-center gap-3 bg-paper rounded-[16px] border border-ink/10 p-3 hover:border-ink/30 transition-colors">
      <Link
        to={`/shul/${shul.id}`}
        className="shrink-0 w-[88px] h-[88px] rounded-[10px] overflow-hidden border border-ink/10 bg-fog"
        tabIndex={-1}
        aria-hidden
      >
        {shul.photoUrl ? (
          <img
            src={shul.photoUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="flex w-full h-full items-center justify-center text-ash/50">
            <Building2 className="w-8 h-8" strokeWidth={1.25} />
          </span>
        )}
      </Link>

      <Link to={`/shul/${shul.id}`} className="min-w-0 flex-1">
        <h2 className="font-semibold text-ink truncate">{name}</h2>

        {(shul.address || distance !== undefined) && (
          <p className="text-sm text-ash mt-0.5 truncate">
            {distance !== undefined && <DistanceText meters={distance} className="font-medium text-ink" />}
            {distance !== undefined && shul.address && ' · '}
            {shul.address}
          </p>
        )}

        {(shul.nusach || hasMessage) && (
          <div className="flex flex-wrap gap-1 mt-2">
            {shul.nusach && (
              <span className="text-xs px-2 py-0.5 rounded-[10px] bg-fog text-ink font-medium">
                {t(`nusach.${shul.nusach}`)}
              </span>
            )}
            {/* Same badge as directory cards (ListingCard). */}
            {hasMessage && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-[10px] bg-weekday-50 text-weekday-700 font-medium">
                <Megaphone className="w-3 h-3" aria-hidden />
                {t('directory.detail.newMessage')}
              </span>
            )}
          </div>
        )}
      </Link>

      {canShowOnMap && (
        <Link
          to={mapHref}
          className="shrink-0 p-1.5 rounded-[10px] text-ash hover:text-ink hover:bg-fog transition-colors"
          aria-label={t('shul.showOnMap')}
          title={t('shul.showOnMap')}
        >
          <MapPin className="w-4 h-4" />
        </Link>
      )}

      <Link to={`/shul/${shul.id}`} className="shrink-0" aria-label={name}>
        <ChevronRight className="w-5 h-5 text-ash rtl:rotate-180" />
      </Link>
    </div>
  );
}
